import { AnyAction } from 'redux';
import { DraftConfiguration, SizePx, SpecialFontTask } from '@/types';
import { createPimpFontTask } from '@/utils/http/createPimpFontDesign';
import { loadImage } from '@/utils/image/loadImage';
import { Thunk } from '@/redux/types';
import { setSpecialFontTaskAction } from '@/components/draft/draftState/actions/setSpecialFontTaskAction';

const specialFontTaskMap = new Map<string, SpecialFontTask>();
const specialFontTaskRequestMap = new Map<string, Promise<SpecialFontTask>>();

const getSpecialFontTaskId = (fontId: string, align: string, text: string) =>
  `${fontId}|${align}|${text}`;

const requestSpecialFont = async (fontId: string, align: string, text: string) => {
  const payload = await createPimpFontTask({
    size: 'M',
    fontId,
    align,
    text,
  });
  const img = await loadImage(payload.url);

  const size: SizePx = { width: img.width * 3, height: img.height * 3, unit: 'px' };

  return {
    taskId: payload.id,
    previewUrl: payload.url,
    size,
  };
};

export const fetchSpecialFontTask =
  (
    fontId: string,
    align: string,
    text: string,
    configuration?: DraftConfiguration
  ): Thunk<Promise<SpecialFontTask>, AnyAction> =>
  async (dispatch, getState) => {
    const specialFontTaskId = getSpecialFontTaskId(fontId, align, text);

    let specialFontTask =
      specialFontTaskMap.get(specialFontTaskId) ||
      (await specialFontTaskRequestMap.get(specialFontTaskId));

    if (!specialFontTask) {
      try {
        const specialFontTaskRequest = requestSpecialFont(fontId, align, text);
        specialFontTaskRequestMap.set(specialFontTaskId, specialFontTaskRequest);

        specialFontTask = await specialFontTaskRequest;
        specialFontTaskRequestMap.delete(specialFontTaskId);
        specialFontTaskMap.set(specialFontTaskId, specialFontTask);
      } catch (error) {
        specialFontTaskRequestMap.delete(specialFontTaskId);
        console.error('creating pimp font task failed!', error);
        throw error;
      }
    }

    if (configuration) {
      getState().draftDispatch(
        setSpecialFontTaskAction(configuration, fontId, align, text, specialFontTask)
      );
    }

    return specialFontTask;
  };

export const getSpecialFontTask = (fontId: string, align: string, text: string) =>
  specialFontTaskMap.get(getSpecialFontTaskId(fontId, align, text));
